import { ref, provide, inject } from "vue";
import type { Ref, InjectionKey } from "vue";

import { getAddons } from "@/api/gql/product-slot-state";
import type { IBookingAddon } from "@/interfaces/store/IBooking";

const FLUSH_MS = 50;

type AddonsApi = {
  addonsByBooking: Ref<Record<string, IBookingAddon[]>>;
  requestForId: (id: string) => boolean;
  clear: () => boolean;
};

const KEY: InjectionKey<AddonsApi> = Symbol("orders-addons");

/**
 * Provide a batched, deduped addons fetcher to descendant cells.
 *
 * Cell components register a booking id via `requestForId`; the call is queued
 * and flushed as a single GraphQL `getAddons([...ids])` after a short
 * debounce. Results are cached in `addonsByBooking` keyed by booking id.
 *
 * `clear` resets the cache (and any pending queue / timer) — call it when the
 * underlying SSearchTable starts a new fetch so cells re-request fresh data.
 *
 * @returns {AddonsApi} Reactive cache + queue handle. Also `provide`d under
 * the internal injection key for child cells via `useOrdersAddonsInject`.
 */
export function useOrdersAddonsProvide(): AddonsApi {
  const addonsByBooking = ref<Record<string, IBookingAddon[]>>({});

  const pendingIds = new Set<string>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let generation = 0;

  /**
   * Queue a booking id for the next batch fetch. No-op when the id is already
   * cached (including a previously-fetched empty result) or already pending.
   *
   * @param {string} id - Booking id to fetch addons for.
   * @returns {boolean} Sentinel.
   */
  function requestForId(id: string): boolean {
    if (id in addonsByBooking.value) {
      return true;
    }

    if (pendingIds.has(id)) {
      return true;
    }

    pendingIds.add(id);

    if (timer) {
      return true;
    }

    timer = setTimeout(flush, FLUSH_MS);

    return true;
  }

  /**
   * Drain the pending queue into one `getAddons` call, fold the response into
   * the cache (incrementing duplicate-category quantities). Called by the
   * debounce timer; safe to call manually for tests.
   *
   * @returns {Promise<void>} Resolves once the cache has been updated.
   */
  async function flush(): Promise<void> {
    timer = null;

    const ids = Array.from(pendingIds);

    pendingIds.clear();

    if (!ids.length) {
      return;
    }

    // Capture the generation at fetch start. If `clear()` is called while we
    // are awaiting `getAddons`, it bumps `generation` — and we discard the
    // result rather than resurrect the pre-clear cache.
    const myGeneration = generation;

    // Pre-seed empty arrays so the cached-check in requestForId returns true
    // even when a booking has zero addons — avoids re-querying on every mount.
    const next = { ...addonsByBooking.value };

    for (const id of ids) {
      next[id] = next[id] ?? [];
    }

    try {
      const res = await getAddons(ids);

      for (const addon of res) {
        if (!addon || !addon.bookingId || !addon.categoryName) {
          continue;
        }

        if (!next[addon.bookingId]) {
          next[addon.bookingId] = [];
        }

        const list = next[addon.bookingId];
        const existing = list.find((a) => a.name === addon.categoryName);

        if (existing) {
          existing.quantity += 1;
        } else {
          list.push({ name: addon.categoryName, quantity: 1 });
        }
      }
    } catch (err) {
      console.error("[useOrdersAddons] fetch failed", err);
      // On error we still commit the pre-seeded empty arrays for these ids:
      // matches pre-migration Orders.vue behaviour and prevents a tight retry
      // loop, at the cost of masking transient failures until clear() runs.
    }

    if (myGeneration !== generation) {
      // clear() ran while we were awaiting — abandon this batch.
      return;
    }

    addonsByBooking.value = next;
  }

  /**
   * Drop all cached addons and any in-flight queue / timer. Used when the
   * search table starts a new fetch and prior addons are likely stale.
   *
   * @returns {boolean} Sentinel.
   */
  function clear(): boolean {
    addonsByBooking.value = {};
    pendingIds.clear();
    generation += 1;

    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    return true;
  }

  const api: AddonsApi = { addonsByBooking, requestForId, clear };

  provide(KEY, api);

  return api;
}

/**
 * Consume the addons API provided by an ancestor `useOrdersAddonsProvide`.
 * Throws if called outside the provider tree (programmer error).
 *
 * @returns {AddonsApi} Reactive cache + queue handle from the provider.
 */
export function useOrdersAddonsInject(): AddonsApi {
  const api = inject(KEY);

  if (!api) {
    throw new Error(
      "useOrdersAddonsInject must be called inside an useOrdersAddonsProvide tree"
    );
  }

  return api;
}
